import axios from 'axios';
import PDFPreview from '../components/PDFPreview';
import { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import '../styles/academic.css';

const Certificates = () => {
    const { currentUser, isAdmin: isRealAdmin } = useAuth();
    const [certificates, setCertificates] = useState([]);
    // Local admin toggle
    const [isAdmin, setIsAdmin] = useState(false);

    useEffect(() => {
        if (isRealAdmin) setIsAdmin(true);
    }, [isRealAdmin]);

    const getYoutubeEmbed = (url) => {
        if (!url) return null;
        const regExp = /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|&v=)([^#&?]*).*/;
        const match = url.match(regExp);
        return (match && match[2].length === 11) ? `https://www.youtube.com/embed/${match[2]}` : null;
    };

    const [showForm, setShowForm] = useState(false);
    const [isEditing, setIsEditing] = useState(false);
    const [editingId, setEditingId] = useState(null);
    const [formData, setFormData] = useState({
        title: '',
        image: '',
        description: '',
        experience: '',
        learnings: '',
        issuer: '',
        dateIssued: '',
        youtubeLink: ''
    });
    const [isUploading, setIsUploading] = useState(false);
    const [selectedImage, setSelectedImage] = useState(null); // Lightbox

    useEffect(() => {
        // fetchUser(); // handled by context
        fetchCertificates();
    }, []);


    const fetchCertificates = async () => {
        try {
            const res = await axios.get('/api/certificates');
            setCertificates(res.data.certificates || []);
        } catch (error) {
            console.error('Error fetching certificates:', error);
        }
    };

    const handleEdit = (cert) => {
        setFormData({
            title: cert.title,
            image: cert.image,
            description: cert.description,
            experience: cert.experience,
            learnings: cert.learnings,
            issuer: cert.issuer,
            dateIssued: cert.dateIssued ? new Date(cert.dateIssued).toISOString().split('T')[0] : '',
            youtubeLink: cert.youtubeLink || ''
        });
        setEditingId(cert._id);
        setIsEditing(true);
        setShowForm(true);
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    const handleImageUpload = async (e) => {
        const file = e.target.files[0];
        if (!file) return;

        const uploadData = new FormData();
        uploadData.append('image', file);
        setIsUploading(true);

        try {
            const res = await axios.post('/api/upload-image', uploadData);
            setFormData({ ...formData, image: res.data.url });
            alert('Image uploaded!');
        } catch (error) {
            const msg = error.response ? error.response.data.error : error.message;
            alert(`Upload failed: ${msg}`);
        } finally {
            setIsUploading(false);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        try {
            if (isEditing && editingId) {
                // UPDATE
                await axios.put(`/api/certificates/${editingId}`, formData);
                alert('Certificate updated!');
            } else {
                // CREATE
                await axios.post('/api/certificates', formData);
                alert('Certificate added!');
            }

            setShowForm(false);
            setIsEditing(false);
            setEditingId(null);
            fetchCertificates();
            setFormData({ title: '', image: '', description: '', experience: '', learnings: '', issuer: '', dateIssued: '', youtubeLink: '' });
        } catch (error) {
            console.error(error);
            const errorMessage = error.response?.data?.error || error.message;
            alert(`Failed to save certificate: ${errorMessage}`);
        }
    };

    const handleDelete = async (id) => {
        if (!window.confirm('Delete this certificate?')) return;
        try {
            await axios.delete(`/api/certificates/${id}`);
            fetchCertificates();
        } catch (error) {
            console.error(error);
        }
    };

    return (
        <div>
            {/* Header & Admin Toggle */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '40px' }}>
                <h1 className="section-title">CERTIFICATES</h1>

                {isRealAdmin && (
                    <button
                        onClick={() => setIsAdmin(!isAdmin)}
                        style={{ background: 'transparent', border: '1px solid black', padding: '5px 10px', fontSize: '0.7em', cursor: 'pointer', fontFamily: 'monospace' }}
                    >
                        [{isAdmin ? 'EXIT_ADMIN' : 'ADMIN'}]
                    </button>
                )}
            </div>

            {/* Admin Console */}
            {isAdmin && (
                <div style={{ marginBottom: '30px', padding: '20px', border: '1px dashed red' }}>
                    <h3 style={{ marginTop: 0 }}>ADMIN_CONSOLE</h3>
                    {!showForm ? (
                        <button onClick={() => setShowForm(true)} className="tag" style={{ cursor: 'pointer' }}>+ NEW CERTIFICATE</button>
                    ) : (
                        <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '15px' }}>
                            <input
                                placeholder="Certificate Title"
                                value={formData.title}
                                onChange={e => setFormData({ ...formData, title: e.target.value })}
                                style={{ padding: '10px', fontFamily: 'inherit' }}
                                required
                            />

                            <div style={{ border: '1px dashed #ccc', padding: '15px' }}>
                                <strong>Certificate Document (Image or PDF)</strong>
                                <input
                                    type="file"
                                    accept="image/*,application/pdf"
                                    onChange={handleImageUpload}
                                    style={{ display: 'block', marginTop: '10px', fontFamily: 'inherit' }}
                                />
                                {isUploading && <p style={{ color: 'blue', fontStyle: 'italic' }}>Uploading...</p>}
                                {formData.image && (
                                    formData.image.toLowerCase().endsWith('.pdf') ? (
                                        <div style={{ marginTop: '10px', border: '1px solid #ddd' }}>
                                            <p style={{ margin: 0, padding: '5px', background: '#eee', fontSize: '0.8rem' }}>PDF Preview:</p>
                                            <iframe src={formData.image} title="Preview" style={{ width: '100%', height: '200px', border: 'none' }} />
                                        </div>
                                    ) : (
                                        <img src={formData.image} alt="Preview" style={{ maxWidth: '200px', marginTop: '10px', border: '1px solid #ddd' }} />
                                    )
                                )}
                            </div>

                            <input
                                placeholder="YouTube Link (Optional)"
                                value={formData.youtubeLink || ''}
                                onChange={e => setFormData({ ...formData, youtubeLink: e.target.value })}
                                style={{ padding: '10px', fontFamily: 'inherit' }}
                            />

                            <textarea
                                placeholder="Description (Optional)"
                                value={formData.description}
                                onChange={e => setFormData({ ...formData, description: e.target.value })}
                                style={{ padding: '10px', fontFamily: 'inherit', minHeight: '80px' }}
                            />

                            <textarea
                                placeholder="Experience (Optional)"
                                value={formData.experience}
                                onChange={e => setFormData({ ...formData, experience: e.target.value })}
                                style={{ padding: '10px', fontFamily: 'inherit', minHeight: '80px' }}
                            />

                            <textarea
                                placeholder="Learnings (Optional)"
                                value={formData.learnings}
                                onChange={e => setFormData({ ...formData, learnings: e.target.value })}
                                style={{ padding: '10px', fontFamily: 'inherit', minHeight: '80px' }}
                            />

                            <input
                                placeholder="Issuer (e.g., Coursera, Google)"
                                value={formData.issuer}
                                onChange={e => setFormData({ ...formData, issuer: e.target.value })}
                                style={{ padding: '10px', fontFamily: 'inherit' }}
                            />

                            <input
                                type="date"
                                value={formData.dateIssued}
                                onChange={e => setFormData({ ...formData, dateIssued: e.target.value })}
                                style={{ padding: '10px', fontFamily: 'inherit' }}
                            />

                            <div style={{ display: 'flex', gap: '10px' }}>
                                <button type="submit" className="tag" style={{ background: 'black', color: 'white', cursor: 'pointer' }}>{isEditing ? 'UPDATE' : 'PUBLISH'}</button>
                                <button type="button" onClick={() => { setShowForm(false); setIsEditing(false); }} className="tag" style={{ cursor: 'pointer' }}>CANCEL</button>
                            </div>
                        </form>
                    )}
                </div>
            )}

            {/* Certificates Grid */}
            <div className="bento-grid">
                {certificates.map(cert => (
                    <div key={cert._id} className="bento-card" style={{ marginBottom: '20px' }}>
                        {/* Title & Date */}
                        <div style={{ marginBottom: '12px' }}>
                            <h3 style={{ margin: '0 0 5px 0', fontSize: '1rem' }}>{cert.title}</h3>
                            {cert.dateIssued && <span style={{ fontSize: '0.75rem', color: '#666' }}>{new Date(cert.dateIssued).toLocaleDateString()}</span>}
                        </div>

                        {/* Condensed Content */}
                        <div style={{ fontSize: '0.85rem', lineHeight: '1.3', marginBottom: '12px' }}>
                            <p style={{ margin: '0 0 8px 0' }}><strong>About:</strong> {cert.description}</p>
                            <p style={{ margin: '0 0 8px 0' }}><strong>Experience:</strong> {cert.experience}</p>
                            <p style={{ margin: '0' }}><strong>Learnings:</strong> {cert.learnings}</p>
                            {cert.issuer && (
                                <p style={{ margin: '8px 0 0 0', fontStyle: 'italic', fontSize: '0.8rem', color: '#666' }}>
                                    — {cert.issuer}
                                </p>
                            )}
                        </div>

                        {/* Certificate Image/PDF - Smaller */}
                        {cert.image && (
                            cert.image.toLowerCase().endsWith('.pdf') ? (
                                <div style={{ marginTop: '10px' }}>
                                    <div style={{
                                        position: 'relative',
                                        // paddingBottom: '75%', // Let PDFPreview determine height
                                        minHeight: '200px',
                                        border: '1px solid #ddd',
                                        background: '#f9f9f9',
                                        cursor: 'pointer',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        overflow: 'hidden'
                                    }} onClick={() => setSelectedImage(cert.image)}>
                                        <PDFPreview file={cert.image} width={300} />
                                        <div style={{ position: 'absolute', bottom: 0, left: 0, width: '100%', background: 'rgba(0,0,0,0.05)', textAlign: 'center', padding: '5px', fontSize: '0.8rem', color: '#555', fontWeight: 'bold' }}>
                                            [CLICK TO OPEN PDF]
                                        </div>
                                    </div>
                                </div>
                            ) : (
                                <img
                                    src={cert.image}
                                    alt={cert.title}
                                    onClick={() => setSelectedImage(cert.image)}
                                    style={{
                                        width: '100%',
                                        height: 'auto',
                                        maxHeight: '200px',
                                        objectFit: 'contain',
                                        cursor: 'pointer',
                                        borderRadius: '4px',
                                        border: '1px solid #ddd',
                                        backgroundColor: '#f9f9f9',
                                        marginTop: '10px'
                                    }}
                                />
                            )
                        )}

                        {/* YouTube Video */}
                        {cert.youtubeLink && getYoutubeEmbed(cert.youtubeLink) && (
                            <div style={{ marginTop: '15px', position: 'relative', paddingBottom: '56.25%', height: 0 }}>
                                <iframe
                                    src={getYoutubeEmbed(cert.youtubeLink)}
                                    style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', border: 0 }}
                                    allowFullScreen
                                    title={cert.title}
                                />
                            </div>
                        )}

                        {/* Admin Actions */}
                        {isAdmin && (
                            <div style={{ marginTop: '10px', fontSize: '0.75rem' }}>
                                <button onClick={() => handleDelete(cert._id)} style={{ color: 'red', cursor: 'pointer', border: 'none', background: 'none', marginRight: '10px' }}>[DELETE]</button>
                                <button onClick={() => handleEdit(cert)} style={{ color: 'blue', cursor: 'pointer', border: 'none', background: 'none' }}>[EDIT]</button>
                            </div>
                        )}
                    </div>
                ))}
            </div>

            {certificates.length === 0 && <p>No certificates yet.</p>}

            {/* Lightbox Modal */}
            {selectedImage && (
                <div
                    className="lightbox-overlay"
                    onClick={() => setSelectedImage(null)}
                >
                    <div className="lightbox-content" style={selectedImage.toLowerCase().endsWith('.pdf') ? { width: '90%', height: '90%', maxWidth: '1000px' } : {}}>
                        {selectedImage.toLowerCase().endsWith('.pdf') ? (
                            <embed
                                src={selectedImage}
                                type="application/pdf"
                                style={{ width: '100%', height: '100%', border: 'none', background: 'white' }}
                            />
                        ) : (
                            <img src={selectedImage} alt="Full view" />
                        )}
                        <button className="lightbox-close" onClick={() => setSelectedImage(null)}>×</button>
                    </div>
                </div>
            )}
        </div>
    );
};

export default Certificates;
